import type { BuildingWing } from '../types';

export const BUILDING_WING_PREFIX: Record<BuildingWing, string> = {
  hotel_wing: 'HW-',
  tower_wing: 'TW-',
};

export const BUILDING_WING_LABEL: Record<BuildingWing, string> = {
  hotel_wing: 'Hotel Wing',
  tower_wing: 'Tower Wing',
};

/** Apply HW- / TW- prefix when the pasted value is a bare unit number (e.g. 101 → HW-101). */
export function formatBuildingUnitLabel(wing: BuildingWing, raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error('Empty unit number');
  }
  const upper = trimmed.toUpperCase();
  const expectedPrefix = BUILDING_WING_PREFIX[wing];
  if (upper.startsWith('HW-') || upper.startsWith('TW-')) {
    if (!upper.startsWith(expectedPrefix)) {
      throw new Error(
        `Unit "${trimmed}" does not match ${BUILDING_WING_LABEL[wing]} (expected prefix ${expectedPrefix})`
      );
    }
    return upper;
  }
  return `${expectedPrefix}${upper}`;
}

/** Infer wing from a stored unit label or legacy unitType. */
export function resolveBuildingWing(params: {
  buildingWing?: BuildingWing;
  unitLabel?: string | null;
  unitType?: 'hotel' | 'apartment';
}): BuildingWing | undefined {
  if (params.buildingWing) return params.buildingWing;
  const label = (params.unitLabel ?? '').trim().toUpperCase();
  if (label.startsWith('HW-')) return 'hotel_wing';
  if (label.startsWith('TW-')) return 'tower_wing';
  if (params.unitType === 'hotel') return 'hotel_wing';
  if (params.unitType === 'apartment') return 'tower_wing';
  return undefined;
}

/** Parse pasted or CSV lines; applies tower prefix when wing is set. */
export function parseBuildingUnitList(raw: string, wing?: BuildingWing): string[] {
  const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const units: string[] = [];
  const seen = new Set<string>();
  const headerWords = new Set(['unit', 'unit_number', 'room', 'apartment', 'unit label', 'wing', 'tower']);
  for (const line of lines) {
    let label = line;
    if (line.includes(',')) {
      const parts = line.split(',').map((p) => p.trim()).filter(Boolean);
      if (parts.length === 0) continue;
      if (headerWords.has(parts[0]!.toLowerCase())) continue;
      label = parts[0]!;
    }
    const formatted = wing ? formatBuildingUnitLabel(wing, label) : label.trim();
    if (!formatted) continue;
    const key = formatted.toLowerCase();
    if (seen.has(key)) {
      throw new Error(`Duplicate unit in list: "${formatted}"`);
    }
    seen.add(key);
    units.push(formatted);
  }
  if (units.length === 0) {
    throw new Error('No unit numbers found. Enter one unit per line (e.g. 101 or HW-101).');
  }
  return units;
}
